import React from 'react';
import { Link } from 'react-router-dom';

function Terms() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-16 text-gray-700">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Terms & Conditions</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">1. Introduction – Who We Are and How to Contact Us</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">Welcome to the website of SALINAS TRADING LLC (“Website”).</li>
          <li className="mb-2">
            This Website is operated by SALINAS TRADING LLC, a Dubai-based company (“we”, “us”, “our”).
          </li>
          <li className="mb-2">
            For questions or complaints about this Website, contact us at{' '}
            <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">
              privacy@salinastrading.com
            </a>
            .
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">2. Our Terms and Conditions</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">These Terms and Conditions (“Terms”) govern your use of the Website. Please read them carefully.</li>
          <li className="mb-2">
            They also govern any purchase of goods or services via the Website, including general trading products and online retail goods.
          </li>
          <li className="mb-2">
            By using or purchasing from this Website, you agree to these Terms. If you do not agree, you must not use the Website.
          </li>
          <li className="mb-2">
            Users must be 18 or over. We reserve the right to limit access if we suspect a user is underage. Proof of age may be required upon delivery, and purchases by users under 18 will be null and void.
          </li>
          <li className="mb-2">
            Our{' '}
            <Link to="/privacy-policy" className="text-blue-600 hover:underline">
              Privacy Policy
            </Link>{' '}
            is incorporated into these Terms and applies to Website use.
          </li>
          <li className="mb-2">
            These Terms and the Privacy Policy form the entire agreement between you and us regarding Website use and purchases. You confirm you have not relied on any other representations. Your statutory rights are unaffected.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">3. Accounts</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            Certain Website features may require creating an account using an email address (“Account”). You can purchase without an account via guest checkout.
          </li>
          <li className="mb-2">
            Account information must be accurate. Do not impersonate others or create accounts for anyone else.
          </li>
          <li className="mb-2">
            Use strong passwords (combining upper/lower-case letters, numbers, symbols) and keep login details confidential. We are not responsible for misuse due to weak passwords or shared credentials.
          </li>
          <li className="mb-2">
            We may suspend, cancel, or edit your Account if we believe it is misused or violates these Terms.
          </li>
          <li className="mb-2">Keep your Account details updated in the Account settings.</li>
          <li className="mb-2">
            You may cancel your Account via Account settings or by contacting us at{' '}
            <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">
              privacy@salinastrading.com
            </a>
            .
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">4. Third-Party Sites</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            The Website may include links to third-party websites (“External Sites”). We do not control or endorse External Sites and are not liable for their content or functionality.
          </li>
          <li className="mb-2">
            External Sites may collect data or send cookies. Review their terms and privacy policies, as you access them at your own risk.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">5. Changes to These Terms</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">We may amend these Terms by posting updates on the Website without prior notice.</li>
          <li className="mb-2">
            Continued use after amendments indicates agreement to the updated Terms. Check regularly for changes.
          </li>
          <li className="mb-2">
            We may change, update, or discontinue Website features. We are not obligated to store user content (e.g., reviews) except as required by law.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">6. Prohibited Activities</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            Users agree to use the Website as permitted by these Terms and not to:
            <ul className="list-disc pl-6 mt-2">
              <li>Damage or impair Website performance.</li>
              <li>Post obscene, defamatory, offensive, or malicious content.</li>
              <li>
                Upload copyrighted material without permission (e.g., SALINAS TRADING LLC branding, images, or text).
              </li>
              <li>Use our brand, logo, or trademarks without written consent.</li>
              <li>Introduce viruses or malicious material.</li>
              <li>Attempt unauthorized access to the Website or connected systems.</li>
              <li>Conduct denial-of-service attacks.</li>
              <li>Circumvent protective measures.</li>
              <li>Use bots, scrapers, or other non-browser tools to access content.</li>
              <li>Harvest information for unlawful purposes.</li>
              <li>Disrupt Website systems or user access.</li>
            </ul>
          </li>
          <li className="mb-2">
            Breaches may be reported to law enforcement, and your right to use the Website will cease immediately.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">7. Content & Ownership of Intellectual Property Rights</h2>
        <p className="leading-relaxed mb-2">
          Intellectual Property Rights (“IPRs”) include patents, copyrights, trademarks, designs, and other rights, whether registered or unregistered.
        </p>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            <strong>Our Content and IPRs</strong>
            <ul className="list-disc pl-6 mt-2">
              <li>
                We own or license all IPRs in Website content (e.g., SALINAS TRADING LLC branding, text, images, layout) unless stated otherwise.
              </li>
              <li>
                Unauthorized use breaches these Terms and may violate our or third-party IPRs. All rights not granted are reserved.
              </li>
            </ul>
          </li>
          <li className="mb-2">
            <strong>Your Content and Our Right to Use It</strong>
            <ul className="list-disc pl-6 mt-2">
              <li>
                Feedback or content (e.g., reviews, photos) you submit (“Feedback Content”) assigns IPRs to us, with moral rights waived.
              </li>
              <li>
                Other submitted content grants us a perpetual, worldwide, royalty-free, transferable license to use (e.g., copy, display, modify) with moral rights waived.
              </li>
              <li>We may use your content without compensation and retain editorial control.</li>
              <li>Your content must be original, accurate, and compliant with these Terms.</li>
              <li>We may remove or block content we deem objectionable or in violation of these Terms.</li>
              <li>We are not obligated to return submitted content unless required by law.</li>
            </ul>
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">8. Order and Payment Process</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            Place orders by selecting products and completing checkout. Prices include 5% VAT per UAE laws. We may change prices without notice, but changes are not retroactive.
          </li>
          <li className="mb-2">Delivery costs are included in the price unless otherwise stated.</li>
          <li className="mb-2">Your order is complete upon receiving our confirmation.</li>
          <li className="mb-2">Pay via the Website or by cash/card on delivery.</li>
          <li className="mb-2">
            Refusing a cash-on-delivery order may result in blacklisting or Account suspension.
          </li>
          <li className="mb-2">
            We aim for timely delivery but are not liable for delays due to incorrect address/contact details or unavailability. Verify details and ensure availability.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">9. Cancellation, Returns, and Refunds</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            Contact our customer service team at{' '}
            <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">
              privacy@salinastrading.com
            </a>{' '}
            to cancel or amend orders.
          </li>
          <li className="mb-2">
            Cancellations require 48 hours’ notice, or you may be charged unless:
            <ul className="list-disc pl-6 mt-2">
              <li>Products are defective or damaged upon delivery.</li>
              <li>You provide photographic evidence, retain packaging, and describe the issue.</li>
            </ul>
            In such cases, we’ll refund or allow transfer to another order.
          </li>
          <li className="mb-2">We are not liable for damage or loss after delivery.</li>
          <li className="mb-2">
            Persistent cancellations without reason may lead to restricted Website access.
          </li>
          <li className="mb-2">
            Returns for non-defective items are subject to our discretion and may incur restocking fees.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">10. Product Safety</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            Follow product instructions to ensure safe use. We are not liable for damage or injury due to improper use.
          </li>
          <li className="mb-2">
            Contact{' '}
            <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">
              privacy@salinastrading.com
            </a>{' '}
            for guidance on product use or safety concerns.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">11. Liability</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            We are liable for foreseeable loss caused directly by our breach of these Terms or failure to use reasonable care.
          </li>
          <li className="mb-2">
            We do not limit liability for death, personal injury, fraud, or where unlawful to do so.
          </li>
          <li className="mb-2">
            Subject to the above, our liability is limited as follows:
            <ul className="list-disc pl-6 mt-2">
              <li>The Website is provided “as is” without warranties (e.g., fitness, performance).</li>
              <li>No guarantee of uninterrupted, secure, or error-free access.</li>
              <li>No warranty on content accuracy or reliability; reliance is at your risk.</li>
              <li>No guarantee against bugs or viruses.</li>
              <li>You are responsible for device configuration and virus protection.</li>
              <li>We are not liable for transmitted information that is accessed or corrupted.</li>
              <li>We may suspend access for maintenance without notice.</li>
            </ul>
          </li>
          <li className="mb-2">
            We may investigate your Website use to comply with laws or terminate access if you violate these Terms.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">12. General</h2>
        <ol className="list-decimal pl-6">
          <li className="mb-2">
            <strong>Severability</strong>: If any Term is invalid, it will be adjusted or removed, but the remaining Terms remain effective.
          </li>
          <li className="mb-2">
            <strong>Waiver</strong>: Our delay in enforcing Terms does not waive our rights to act later.
          </li>
          <li className="mb-2">
            <strong>Transfer</strong>: We may transfer our contract with you to a third party, ensuring your rights are unaffected.
          </li>
          <li className="mb-2">
            <strong>Third-Party Rights</strong>: These Terms are between you and us only; no third party has rights.
          </li>
          <li className="mb-2">
            <strong>Force Majeure</strong>: We are not liable for delays or failures due to events beyond our control (e.g., acts of God, internet failure, government actions). We may postpone, cancel, or refund as deemed fit.
          </li>
          <li className="mb-2">
            <strong>Governing Law and Jurisdiction</strong>: These Terms are governed by the laws of the Dubai International Financial Centre (“DIFC”). The DIFC Courts have sole jurisdiction, though Dubai Civil Courts may apply if mandated.
          </li>
        </ol>
      </section>

      <p className="text-sm text-gray-500">Last updated: April 18, 2025</p>
    </div>
  );
}

export default Terms;